// Application Insights Configuration
// =================================
// Centralized configuration for Application Insights error tracking behavior

export interface AppInsightsConfig {
  // Whether to track different types of HTTP errors as exceptions
  trackAuthErrorsAsExceptions: boolean;
  trackValidationErrorsAsExceptions: boolean;
  trackNotFoundErrorsAsExceptions: boolean;
  trackRateLimitErrorsAsExceptions: boolean;
  
  // Whether to track HTTP errors as events instead of exceptions
  trackHttpErrorsAsEvents: boolean;
  
  // Minimum log level for different environments
  minLogLevel: {
    development: number;
    staging: number;
    production: number;
  };
  
  // HTTP status codes that should never be tracked as exceptions
  excludeStatusCodes: number[];
}

// Default configuration
export const defaultAppInsightsConfig: AppInsightsConfig = {
  // Don't track these as exceptions - they're expected business logic errors
  trackAuthErrorsAsExceptions: false,        // 401, 403
  trackValidationErrorsAsExceptions: false,  // 400, 422
  trackNotFoundErrorsAsExceptions: false,    // 404
  trackRateLimitErrorsAsExceptions: false,   // 429
  
  // Track them as events instead for monitoring
  trackHttpErrorsAsEvents: true,
  
  // Minimum log levels by environment
  minLogLevel: {
    development: 0, // Verbose - log everything in development
    staging: 1,     // Information - log info and above in staging
    production: 2,  // Warning - only log warnings and above in production
  },
  
  // Status codes to never track as exceptions
  excludeStatusCodes: [400, 401, 403, 404, 422, 429],
};

// Get current configuration based on environment
export const getAppInsightsConfig = (): AppInsightsConfig => {
  // You could load this from window.config or environment variables in the future
  return defaultAppInsightsConfig;
};

// Helper function to check if a status code should be tracked as exception
export const shouldTrackStatusAsException = (statusCode: number): boolean => {
  const config = getAppInsightsConfig();
  return !config.excludeStatusCodes.includes(statusCode);
};

// Helper function to get minimum log level for current environment
export const getMinLogLevelForEnvironment = (): number => {
  const config = getAppInsightsConfig();
  const env = (window.config?.APP_ENV || window.config?.ENVIRONMENT_NAME || 'development').toLowerCase();
  
  if (env.includes('prod')) return config.minLogLevel.production;
  if (env.includes('stag')) return config.minLogLevel.staging;
  return config.minLogLevel.development;
};